import { BulkInsertResponse, DataAccess } from "../../dataaccess/data.access";

export type AcctRatingRuleMode = "ALLOW" | "WARN" | "DENY";

export type AcctRatingRuleCode =
    | "CREATE_EVENTS"
    | "FUNDING_REQUESTS"
    | "EDIT_ORG_DETAILS"
    | "MANAGE_MEMBERS"
    | "MANAGE_NEWS"
    | "LOG_SERVICE_HOURS"
    | "SHOW_ORG_PAGE";

export interface AcctRatingRule {
    acctRatingId?: number;
    ruleCode: AcctRatingRuleCode;
    modeCode: AcctRatingRuleMode;
    message?: string;
}

export const ACCT_RATING_RULE_CODES: ReadonlyArray<{
    code: AcctRatingRuleCode;
    label: string;
    help: string;
}> = [
    {
        code: "CREATE_EVENTS",
        label: "Create events",
        help: "Blocks or warns when the organization requests new events.",
    },
    {
        code: "FUNDING_REQUESTS",
        label: "Funding requests",
        help: "Controls budget viewing and funding request submission.",
    },
    {
        code: "EDIT_ORG_DETAILS",
        label: "Edit organization details",
        help: "Controls editing organization profile details and attributes.",
    },
    {
        code: "MANAGE_MEMBERS",
        label: "Manage members",
        help: "Controls adding, removing, or changing organization membership.",
    },
    {
        code: "MANAGE_NEWS",
        label: "Manage news",
        help: "Controls creating and editing organization news articles.",
    },
    {
        code: "LOG_SERVICE_HOURS",
        label: "Log service hours",
        help: "Controls logging service hours against this organization.",
    },
];

export interface AcctRating {
    acctRatingId: number;
    ratingName: string;
    sortOrder?: number;
    defnState?: number;
    rules: AcctRatingRule[];
}

export interface AcctRatingsResponse {
    ratings: AcctRating[];
}

export function changedAcctRatingRules(draftRatings: AcctRating[], savedRatings: AcctRating[]): AcctRatingRule[] {
    const savedByRatingId = new Map(savedRatings.map((rating) => [rating.acctRatingId, rating]));
    const changedRules: AcctRatingRule[] = [];

    for (const draftRating of draftRatings) {
        const savedRating = savedByRatingId.get(draftRating.acctRatingId);
        for (const draftRule of draftRating.rules ?? []) {
            const savedRule = savedRating?.rules?.find((rule) => rule.ruleCode === draftRule.ruleCode);
            if (serializeRule(draftRule, draftRule.ruleCode) !== serializeRule(savedRule, draftRule.ruleCode)) {
                changedRules.push({
                    acctRatingId: draftRating.acctRatingId,
                    ruleCode: draftRule.ruleCode,
                    modeCode: draftRule.modeCode,
                    message: draftRule.modeCode === "WARN" ? draftRule.message : undefined,
                });
            }
        }
    }

    return changedRules;
}

function serializeRule(rule: AcctRatingRule | undefined, ruleCode: AcctRatingRuleCode): string {
    const modeCode = rule?.modeCode ?? "ALLOW";
    const message = modeCode === "WARN" ? (rule?.message ?? null) : null;
    return JSON.stringify({ ruleCode, modeCode, message });
}

/** Runtime org status effects (separate endpoint; not part of rating config CRUD). */
export type OrgStatusEffectMode = AcctRatingRuleMode;

export type OrgStatusFunction = AcctRatingRuleCode;

export interface OrgStatusEffect {
    function: OrgStatusFunction;
    mode: OrgStatusEffectMode;
    blocksAccess: boolean;
    showMessage: boolean;
    message?: string;
    adminOverrideApplied?: boolean;
    ratingName?: string;
    ratingId?: number;
}

export interface OrgStatusEffectsResponse {
    orgId: number;
    contactId: number;
    statusEffects: OrgStatusEffect[];
    statusMessages: string[];
}

export type OrgDetailsSection = "details" | "budgets" | "events" | "news" | "members" | "serviceHours";

export const ORG_SECTION_STATUS_FUNCTIONS: Partial<Record<OrgDetailsSection, OrgStatusFunction[]>> = {
    details: ["EDIT_ORG_DETAILS"],
    budgets: ["FUNDING_REQUESTS"],
    events: ["CREATE_EVENTS"],
    news: ["MANAGE_NEWS"],
    members: ["MANAGE_MEMBERS"],
    serviceHours: ["LOG_SERVICE_HOURS"],
};

export function statusMessagesForSection(statusEffects: OrgStatusEffect[], section: OrgDetailsSection): string[] {
    const functions = ORG_SECTION_STATUS_FUNCTIONS[section] ?? [];
    return statusEffects
        .filter((effect) => functions.includes(effect.function) && effect.showMessage && effect.message)
        .map((effect) => effect.message as string);
}

export class SeOrgStatusService {
    public static async loadConfigs(): Promise<AcctRating[]> {
        const response = await DataAccess.get<AcctRatingsResponse>(
            DataAccess.injectCaller("/org/rating/admin.json", "SeOrgStatusService.loadConfigs"),
        );
        return response?.ratings ?? [];
    }

    public static async saveConfigs(rules: AcctRatingRule[]): Promise<BulkInsertResponse> {
        return DataAccess.put<BulkInsertResponse>(
            DataAccess.injectCaller("/org/rating/admin.json", "SeOrgStatusService.saveConfigs"),
            { rules },
        );
    }

    public static async resolveEffects(orgId: number): Promise<OrgStatusEffectsResponse> {
        const params = DataAccess.params({ orgId });
        return DataAccess.get<OrgStatusEffectsResponse>(
            DataAccess.injectCaller(`/org/rating/effects.json?${params}`, "SeOrgStatusService.resolveEffects"),
        );
    }
}
